import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { ServiceAppointment } from '../data-models/service-appointment.model';
import { ServiceAppointmentCancelRequest } from '../request-models/service-appointment-cancel-request.model';
import { ServiceAppointmentCreate } from '../request-models/service-appointment-create.model';
import { ServiceAppointmentListQuery } from '../request-models/service-appointment-list-query.model';
import { Page } from '../response-models/page.model';
import { ServiceAppointmentStatusCounts } from '../response-models/service-appointment-status-counts.model';
import { isRecord } from '../utils/is-record';
import { toServiceAppointmentListQuery, toServiceAppointmentRequest } from '../utils/service-appointment-request-utils';
import { countOrZero } from '../utils/wire-value-utils';

/**
 * Service appointments: `/service-appointments[/:id[/cancel]]` and `/service-appointments/organization/:id[/counts]`
 * (hub `user-service-appointments-controller.ts`).
 *
 * Every method rejects with an `ApiError`; a Core org gets `code === 'pro_required'`.
 */
@Injectable({
  providedIn: 'root'
})
export class ServiceAppointmentService extends BaseService<ServiceAppointment> {
  constructor() {
    super('service-appointments', ServiceAppointment);
  }

  /** One page of an organization's appointments, optionally filtered by status (`page` / `limit` paging). */
  async getOrganizationAppointments(organizationId: string, query?: ServiceAppointmentListQuery): Promise<Page<ServiceAppointment>> {
    const opts = { path: ['organization', organizationId], query: toServiceAppointmentListQuery(query) };
    return firstValueFrom(this.getPage(opts));
  }

  /** Appointment counts per status for an organization. */
  async getOrganizationAppointmentCounts(organizationId: string): Promise<ServiceAppointmentStatusCounts> {
    const opts = { path: ['organization', organizationId, 'counts'] };
    return firstValueFrom(this.request('GET', opts, toStatusCounts));
  }

  /** One appointment, or `null` when the hub returns none. */
  async getAppointment(id: string): Promise<ServiceAppointment | null> {
    return firstValueFrom(this.get(id));
  }

  /** Request a service appointment (with already-uploaded attachments). */
  async createAppointment(data: ServiceAppointmentCreate): Promise<ServiceAppointment> {
    const created = await firstValueFrom(this.create(toServiceAppointmentRequest(data)));
    if (!created) {
      throw new Error('The hub returned no service appointment');
    }
    return created;
  }

  /** Cancel an appointment; the hub requires a reason. */
  async cancelAppointment(id: string, reason: string): Promise<void> {
    const body: ServiceAppointmentCancelRequest = { reason };
    await firstValueFrom(this.request('PATCH', { id, path: 'cancel' }, () => undefined, body));
  }
}

function toStatusCounts(data: unknown): ServiceAppointmentStatusCounts {
  const source = isRecord(data) ? data : {};
  return {
    pending: countOrZero(source['pending']),
    requested: countOrZero(source['requested']),
    scheduled: countOrZero(source['scheduled']),
    confirmed: countOrZero(source['confirmed']),
    in_progress: countOrZero(source['in_progress']),
    completed: countOrZero(source['completed']),
    cancelled: countOrZero(source['cancelled']),
  };
}
